<template>
	<vuejs-datepicker 
		id="sprintful-datepicker"
		:monday-first="mondayFirstOption"
		:maximum-view="maximumView"
		:disabledDates="disabledDates"
		:inline="true"
		:value="date"
		:language="datePickerLanguagePacks[activeLocale]"
		@selected="selectDate"
		@changedMonth="monthChanged"
	></vuejs-datepicker>
</template>

<script>
import VuejsDatepicker from "vuejs-datepicker"
import { mapGetters, mapMutations } from "vuex"
import {en, es, fr, nbNO, zh, de, is, nl, sv, ja, cs, ar, pl, ru, it, fi, sk, el, da, ro } from 'vuejs-datepicker/dist/locale'
import {localization} from "../../mixins/localization.js"
import calendarViewSpecific from "../../mixins/calendarViewSpecific.js"

export default {
	components: { VuejsDatepicker },

	mixins: [localization, calendarViewSpecific],
	
	data() {
		return {
			maximumView: "day",
			lastActiveRequest: null,
			disabledDates: {
				customPredictor: this.customPredictor
			},
			datePickerLanguagePacks: {
				en: en,
				cn: zh,
				es: es,
				sp: es,
				no: nbNO,
				fr: fr,
				is: is,
				de: de,
				nl: nl,
				se: sv,
				ja: ja,
				cs: cs,
				ar: ar,
				pl: pl,
				ru: ru,
				it: it,
				fi: fi,
				sk: sk,
				el: el,
				da: da,
				ro: ro
			}
		}
	},

	computed: {
		...mapGetters([
			'date',
			'schedulingData',
			'calendarData',
			'availabilitySlots',
			'sprintfulBaseUrl',
			'isInitialDataFetched'
		]),

		mondayFirstOption() {
			return this.schedulingData.firstDayOfWeek === 1;
		}
	},

	mounted() {
		// To fetch initial events for the current month
		if (!this.isInitialDataFetched) {
			var date = moment().startOf('month');

			// chack if query params have date=???
			let dateParam = this.$root.getURLQueryValue("date");
			if (dateParam) {
				try {
					if (moment(dateParam, 'DD-MM-YYYY').isValid()) {
						date = moment(dateParam, 'DD-MM-YYYY');
					}
				} catch (e) {}
			}

			this.monthChanged(date);
			this.setIsInitialDataFetched(true);
		}
	},

	methods: {
		...mapMutations([
			'setIsSlotAvailable',
			'setIsBookingAvailable',
			'setDate',
			'setLoggedInUserEmail',
			'setAvailabilitySlots',
			'setLoadingSlotsFlag',
			'setAvailabilityGeneratedAt',
			'setActiveView',
			'setIsInitialDataFetched'
		]),

		selectDate(date) {
			if (window.innerWidth <= 480) { document.getElementById("sprintful-time-select-list").scrollIntoView({ block: 'start',  behavior: 'smooth' }) } //scrolling to time list on selecting date in mobile view
 
			this.setDate(date)
			this.setIsSlotAvailable(true);
			this.setIsBookingAvailable(true);
		},

		monthChanged(updatedMonthFirstDate) {

			this.setLoadingSlotsFlag(true); // Setting loading flag

			// Setting params to fetch slots for next month
			let startOfUpdatedMonth = moment(updatedMonthFirstDate).format("YYYY-MM-DD HH:mm:ss UTC");
			let endOfUpdatedMonth = moment(updatedMonthFirstDate).endOf('month').format("YYYY-MM-DD HH:mm:ss UTC");

			// Preventing frequent requests
			if (this.lastActiveRequest) {
				this.lastActiveRequest.abort()
			}

			this.lastActiveRequest = $.ajax({
				url: `${this.sprintfulBaseUrl}/pages/get_availability`,
				type: "GET",
				dataType: 'json',
				data: {
					start_date: startOfUpdatedMonth,
					end_date: endOfUpdatedMonth,
					calendar_slug: this.calendarData.slug
				},

				xhrFields: {
					withCredentials: true
				},

				success: (data) => {
					if (data.success) {
						this.setAvailabilityGeneratedAt(data.generated_at);
						this.setAvailabilitySlots(data.availability_slots);
						this.setLoggedInUserEmail(data.logged_in_user_email)
	
						// Resetting flag to be re-calculated for new date/data
						this.setIsSlotAvailable(true);
						this.setIsBookingAvailable(true);
						this.setLoadingSlotsFlag(false);
					} else {
						this.setLoggedInUserEmail(data.logged_in_user_email)
						this.setActiveView('unavailable');
						this.setLoadingSlotsFlag(false);
					}
				},
			})
		},
		
		customPredictor(date) {
			const today = moment();
			const mDate = moment(date);
			const lookAhead = parseInt(this.schedulingData.lookAhead) || 2592000;
			const maxDate = today.clone().add(lookAhead, "seconds");
			const calType = this.calendarData.settings.calendarType;

			// check if the current date is before now
			today.set({hour:0,minute:0,second:0,millisecond:0});

			if (mDate.isBefore(today)) {
				return true;
			}

			if (mDate.isAfter(maxDate)) {
				return true;
			}

			// return availability based on week availability settings
			// for auto availability and not team calendars
			if (this.schedulingData.type == "auto" && calType !== "teams") {
				const weekday = mDate.format("dddd");
				const weekdaySettings = _.find(this.schedulingData.days, { name: weekday });

				if (!weekdaySettings.available) {
					return true;
				}
			}

			// for manual availability and not team calendars
			if (this.schedulingData.type == "manual" && calType !== "teams") {
				const weekday = parseInt(mDate.format("d"));

				// checking if any of recurring event is available for today;
				var availability = this.schedulingData.recurring.find((x) => x.daysOfWeek.includes(weekday));
				
				if (!availability) {
					return true
				}
			}

			// Check if no slot available for the selected appointment this day.
			if (!this.isAnySlotAvailableForTheDay(date)) {
				return true;
			}

			return false
		}
	}
}
</script>

<style>
.vdp-datepicker,
.vdp-datepicker__calendar {
	height: 100%;
	border: 0px solid rgba(204, 204, 204, 0.3098) !important;
	background-color: transparent !important;
	width: 330px !important;
}

@media(max-width: 480px) {
	.vdp-datepicker, .vdp-datepicker__calendar {
		width: 375px !important;
	}
}

@media(max-width: 380px) {
	.vdp-datepicker, .vdp-datepicker__calendar {
		width: 345px !important;
	}
}

@media(max-width: 320px) {
	.vdp-datepicker, .vdp-datepicker__calendar {
		width: 295px !important;
	}
}
</style>